import { SxProps, Theme } from '@mui/material';
import { Color } from 'shared/utils/colors';
import { REPORT_SELECTABLE_CONTROL_LABEL_SX } from '../reportSegmentedControlStyles';

/** Horizontal padding of report dialog content — keep in sync with dialog.tsx */
export const REPORT_DIALOG_PADDING_X = 3;

/** Bleed dividers / borders to the dialog content edge (parent must use px: REPORT_DIALOG_PADDING_X). */
export const REPORT_MODAL_EDGE_BLEED_SX: SxProps<Theme> = {
  mx: -REPORT_DIALOG_PADDING_X,
};

const SECTION_SUBHEADER_SX: SxProps<Theme> = {
  typography: 'subtitle2',
  fontWeight: 600,
  letterSpacing: '0.5px',
  color: Color.textActive,
  pt: 2,
  pb: 1.5,
};

export const REPORT_RECIPIENT_SECTION_SX: SxProps<Theme> = {
  gridColumn: '1 / -1',
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  columnGap: 4,
  rowGap: 1,
  '& .MuiFormControl-root': {
    margin: 0,
    width: '100%',
  },
};

export const REPORT_FORM_GRID_SX: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  columnGap: 4,
  rowGap: 2,
  width: '100%',
  alignItems: 'start',
  '& .MuiFormControl-root': {
    margin: 0,
    width: '100%',
  },
};

export const REPORT_FORM_FULL_WIDTH_SX: SxProps<Theme> = {
  gridColumn: '1 / -1',
};

export const REPORT_CONTENT_LIST_SX: SxProps<Theme> = {
  border: 1,
  borderColor: Color.blackWithOpacity,
  borderRadius: '12px',
  overflow: 'hidden',
  bgcolor: 'background.paper',
};

export const REPORT_CONTENT_LIST_ITEM_SX: SxProps<Theme> = {
  px: 2,
  '&:not(:last-of-type)': {
    borderBottom: 1,
    borderColor: Color.blackWithOpacity,
  },
};

export const REPORT_CONTENT_SECTION_ICON_SIZE = 22;

export const REPORT_CONTENT_SUBOPTIONS_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 1.5,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
};

export const REPORT_SUBOPTION_ROW_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: 0.75,
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
};

export const REPORT_SUBOPTION_LABEL_SX: SxProps<Theme> = {
  typography: 'caption',
  color: 'text.secondary',
  fontWeight: 500,
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 20,
};

export const REPORT_FILTER_CHIP_LABEL_SX: SxProps<Theme> = {
  ...REPORT_SELECTABLE_CONTROL_LABEL_SX,
  position: 'relative',
  display: 'inline-block',
  whiteSpace: 'nowrap',
  '&::after': {
    content: 'attr(data-label)',
    display: 'block',
    height: 0,
    overflow: 'hidden',
    visibility: 'hidden',
    fontWeight: 500,
  },
};

export const REPORT_FILTER_CHIP_SX: SxProps<Theme> = {
  ...REPORT_SELECTABLE_CONTROL_LABEL_SX,
  height: 'auto',
  borderRadius: '8px',
  borderWidth: 1,
  borderStyle: 'solid',
  '& .MuiChip-label': {
    ...REPORT_SELECTABLE_CONTROL_LABEL_SX,
    px: 1.5,
    py: 0.75,
    pb: 0,
  },
};

export const SECTION_SUBHEADER_STYLE = SECTION_SUBHEADER_SX;

export function reportListItemCollapseDetailsSx(theme: Theme, opacity: number) {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 1.5,
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    opacity,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shortest,
    }),
  };
}

export function getContentDimOpacity(
  theme: Theme,
  isUnavailable: boolean,
  switchChecked: boolean
): number {
  if (isUnavailable) {
    return theme.palette.action.disabledOpacity;
  }
  return switchChecked ? 1 : 0.45;
}

export const reportContentSwitchSx = (
  isInteractive: boolean
): SxProps<Theme> => ({
  flexShrink: 0,
  mt: 0.25,
  cursor: isInteractive ? 'pointer' : 'default',
  '& .MuiSwitch-switchBase': {
    cursor: isInteractive ? 'pointer' : 'default',
    // Two-tone like MUI's default coloured switch: a solid brand-yellow thumb
    // riding a paler yellow track (previously both were the same soft yellow,
    // which read as one uniform blob).
    '&.Mui-checked': {
      color: Color.primaryOrange,
      '& + .MuiSwitch-track': {
        backgroundColor: Color.primaryOrangePale,
        opacity: 1,
      },
    },
    '&.Mui-checked:hover': {
      backgroundColor: Color.primaryOrangeLight,
      '& + .MuiSwitch-track': {
        backgroundColor: Color.primaryOrangePale,
        opacity: 1,
      },
    },
  },
});
